import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  AddItemsDrawer,
  type DrawerItem,
} from '@/components/shared/AddItemsDrawer'
import { MoveListItem } from '@/components/shared/MoveListItem'
import { InlineEmptyState } from '@/components/shared/InlineEmptyState'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { useToast } from '@/hooks/use-toast'
import { useCollection } from '@/hooks/useCollection'
import { useCreateSeries } from '@/hooks/useCreateSeries'
import { useAddMovesToSeries } from '@/hooks/useAddMovesToSeries'
import { useMoves } from '@/hooks/useMoves'
import { useTagsList } from '@/hooks/useTagsList'
import { useAddTagsToEntity } from '@/hooks/useTaggings'

export function CreateSeriesPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const selectedMoveIds = useCollection<string>([])
  const selectedTagIds = useCollection<string>([])
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isTagDrawerOpen, setIsTagDrawerOpen] = useState(false)

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Fetch data from API
  const createSeriesMutation = useCreateSeries()
  const addMovesToSeriesMutation = useAddMovesToSeries()
  const addTagsMutation = useAddTagsToEntity()
  const { data: movesData, isLoading: movesLoading } = useMoves({ limit: 100 })
  const { data: tagsData, isLoading: tagsLoading } = useTagsList({ limit: 100 })

  // Get all available moves and tags
  const availableMoves = movesData?.moves || []
  const availableTags = tagsData?.tags || []

  // Get selected moves for display
  const selectedMoves = availableMoves.filter(move =>
    selectedMoveIds.hasItem(move.id)
  )

  // Get selected tags for display
  const selectedTags = availableTags.filter(tag =>
    selectedTagIds.hasItem(tag.id)
  )

  const handleRemoveMove = (moveId: string) => {
    const index = selectedMoveIds.items.indexOf(moveId)
    if (index !== -1) {
      selectedMoveIds.removeItemAt(index)
    }
  }

  const handleAddMoves = (moveIds: string[]) => {
    // Add new move IDs, avoiding duplicates
    const newMoveIds = moveIds.filter(id => !selectedMoveIds.hasItem(id))
    selectedMoveIds.addItems(newMoveIds)
  }

  const handleRemoveTag = (tagId: string) => {
    const index = selectedTagIds.items.indexOf(tagId)
    if (index !== -1) {
      selectedTagIds.removeItemAt(index)
    }
  }

  const handleAddTags = (tagIds: string[]) => {
    // Add new tag IDs, avoiding duplicates
    const newTagIds = tagIds.filter(id => !selectedTagIds.hasItem(id))
    selectedTagIds.addItems(newTagIds)
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()

    // Validate data
    if (!name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Series name is required',
        variant: 'destructive',
      })
      return
    }

    if (!description.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Description is required',
        variant: 'destructive',
      })
      return
    }

    if (selectedMoveIds.items.length === 0) {
      toast({
        title: 'Validation Error',
        description: 'At least one move is required',
        variant: 'destructive',
      })
      return
    }

    try {
      // Step 1: Create the series (navigation happens in hook)
      const newSeries = await createSeriesMutation.mutateAsync({
        name: name.trim(),
        description: description.trim(),
      })

      // Step 2: Add moves to the series
      for (const moveId of selectedMoveIds.items) {
        await addMovesToSeriesMutation.mutateAsync({
          seriesId: newSeries.id,
          moveId,
        })
      }

      // Step 3: Add tags if any selected
      if (selectedTagIds.items.length > 0) {
        await addTagsMutation.mutateAsync({
          entityType: 'series',
          entityId: newSeries.id,
          data: { tagIds: selectedTagIds.items },
        })
      }

      // Navigation to series detail page happens automatically in useCreateSeries hook
    } catch (error) {
      // Error handling is done in the mutation hooks
      console.error('Failed to create series:', error)
    }
  }

  return (
    <>
      <div className="min-h-screen bg-[#F9F9F9]">
        {/* Main Content */}
        <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
          {(movesLoading || tagsLoading) && <LoadingSpinner size="lg" />}

          {!movesLoading && !tagsLoading && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info Section */}
              <section className="rounded-2xl border border-[#e5e7eb] bg-white p-6">
                {/* Header and Action Buttons */}
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-black">
                    Series Information
                  </h2>
                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/series')}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      disabled={
                        !name.trim() ||
                        !description.trim() ||
                        selectedMoveIds.items.length === 0 ||
                        createSeriesMutation.isPending
                      }
                      className="bg-[#0033A0] hover:bg-[#002080]"
                    >
                      {createSeriesMutation.isPending
                        ? 'Creating...'
                        : 'Create Series'}
                    </Button>
                  </div>
                </div>
                <div className="space-y-4">
                  {/* Name Input */}
                  <div>
                    <label
                      htmlFor="series-name"
                      className="mb-1.5 block text-sm font-medium text-black"
                    >
                      Series Name <span className="text-[#D62828]">*</span>
                    </label>
                    <Input
                      id="series-name"
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Enter series name"
                      maxLength={255}
                      required
                      className="w-full"
                      aria-required="true"
                    />
                    <p className="mt-1 text-xs text-[#666666]">
                      {name.length}/255 characters
                    </p>
                  </div>

                  {/* Description Textarea */}
                  <div>
                    <label
                      htmlFor="series-description"
                      className="mb-1.5 block text-sm font-medium text-black"
                    >
                      Description <span className="text-[#D62828]">*</span>
                    </label>
                    <Textarea
                      id="series-description"
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      placeholder="Describe the series and what moves it covers"
                      maxLength={1000}
                      required
                      rows={4}
                      className="w-full resize-none"
                      aria-required="true"
                    />
                    <p className="mt-1 text-xs text-[#666666]">
                      {description.length}/1000 characters
                    </p>
                  </div>
                </div>
              </section>

              {/* Moves Section */}
              <section className="rounded-2xl border border-[#e5e7eb] bg-white p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-base font-semibold text-black">
                    Moves <span className="text-[#D62828]">*</span>
                  </h2>
                  <Button
                    type="button"
                    onClick={() => setIsDrawerOpen(true)}
                    className="bg-[#0033A0] text-white hover:bg-[#0033A0]/90"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Moves
                  </Button>
                </div>

                {selectedMoves.length === 0 ? (
                  <InlineEmptyState
                    emoji="🥋"
                    message="No moves added yet. Add at least one move to create this series."
                  />
                ) : (
                  <div className="space-y-3">
                    {selectedMoves.map(move => (
                      <MoveListItem
                        key={move.id}
                        id={move.id}
                        name={move.name}
                        description={move.description}
                        thumbnailUrl={move.thumbnailUrl}
                        showThumbnail={true}
                        onRemove={handleRemoveMove}
                      />
                    ))}
                  </div>
                )}
              </section>

              {/* Tags Section */}
              <section className="rounded-2xl border border-[#e5e7eb] bg-white p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-base font-semibold text-black">
                    Tags
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      (Optional)
                    </span>
                  </h2>
                  <Button
                    type="button"
                    onClick={() => setIsTagDrawerOpen(true)}
                    className="bg-[#0033A0] text-white hover:bg-[#0033A0]/90"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Tags
                  </Button>
                </div>

                {selectedTags.length === 0 ? (
                  <InlineEmptyState
                    emoji="🏷️"
                    message="No tags added yet"
                    secondaryMessage='Click "Add Tags" to categorize this series'
                  />
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {selectedTags.map(tag => (
                      <div
                        key={tag.id}
                        className="flex items-center gap-1 rounded-full px-3 py-1.5 text-sm text-white"
                        style={{
                          backgroundColor: tag.color || '#0033A0',
                        }}
                      >
                        {tag.name}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag.id)}
                          className="ml-1 rounded-full hover:bg-white/20"
                          aria-label={`Remove ${tag.name} tag`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </form>
          )}
        </main>
      </div>

      {/* Add Moves Drawer */}
      <AddItemsDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Add Moves to Series"
        items={availableMoves as DrawerItem[]}
        currentItemIds={selectedMoveIds.items}
        onAddItems={handleAddMoves}
        searchPlaceholder="Search moves..."
        addButtonLabel="Add Selected Moves"
        emptyMessage="No moves available"
        emptySearchMessage="No moves match your search"
      />

      {/* Add Tags Drawer */}
      <AddItemsDrawer
        isOpen={isTagDrawerOpen}
        onClose={() => setIsTagDrawerOpen(false)}
        title="Add Tags to Series"
        items={availableTags.map(
          (tag): DrawerItem => ({
            id: tag.id,
            name: tag.name,
            description: '',
            color: tag.color,
          })
        )}
        currentItemIds={selectedTagIds.items}
        onAddItems={handleAddTags}
        searchPlaceholder="Search tags..."
        addButtonLabel="Add Selected Tags"
        emptyMessage="No tags available"
        emptySearchMessage="No tags match your search"
      />
    </>
  )
}
